import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Clock, Search, XCircle } from 'lucide-react';
import { TransactionListItem } from '@/components/transactions/TransactionListItem';
import { TransactionReceiptModal } from '@/components/transactions/TransactionReceiptModal';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { userTransactionService, getApiErrorMessage } from '@/services';
import type { TransactionCategory, TransactionItem, TransactionStatus, TransactionType } from '@/types/transactions';
import { notify } from '@/utils/notify';

const CATEGORY_OPTIONS: Array<{ value: TransactionCategory | ''; label: string }> = [
  { value: '', label: 'All categories' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'airtime', label: 'Airtime' },
  { value: 'data', label: 'Data' },
  { value: 'bills', label: 'Bills' },
  { value: 'verification', label: 'Verification' },
];

export function UserTransactionsPage() {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<TransactionItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 1 });
  const [summary, setSummary] = useState({ totalCount: 0, successCount: 0, failedCount: 0, pendingCount: 0 });
  const [selected, setSelected] = useState<TransactionItem | null>(null);

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [category, setCategory] = useState<TransactionCategory | ''>('');
  const [status, setStatus] = useState<TransactionStatus | ''>('');
  const [type, setType] = useState<TransactionType | ''>('');
  const [period, setPeriod] = useState<'7d' | '30d' | 'all'>('30d');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        userTransactionService.list({
          page: pagination.page,
          limit: pagination.limit,
          search: search || undefined,
          category: category || undefined,
          status: status || undefined,
          type: type || undefined,
          period,
        }),
        userTransactionService.summary(),
      ]);
      setTransactions(listRes.data?.transactions ?? []);
      setPagination(listRes.data?.pagination ?? pagination);
      setSummary(summaryRes.data?.summary ?? summary);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load transactions'));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, category, status, type, period]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPagination((p) => ({ ...p, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const statCards = [
    { label: 'All transactions', value: summary.totalCount, icon: null, tone: 'text-brand-navy' },
    { label: 'Successful', value: summary.successCount, icon: CheckCircle2, tone: 'text-emerald-600' },
    { label: 'Failed', value: summary.failedCount, icon: XCircle, tone: 'text-red-500' },
    { label: 'Pending', value: summary.pendingCount, icon: Clock, tone: 'text-amber-600' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-brand-navy">Transactions</h1>
        <p className="text-sm text-brand-muted">View your transaction history and receipts</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {statCards.map(({ label, value, icon: Icon, tone }) => (
          <Card key={label} className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs text-brand-muted">{label}</p>
                <p className={`mt-1 text-2xl font-bold tabular-nums ${tone}`}>{value}</p>
              </div>
              {Icon && (
                <div className={`rounded-lg bg-gray-50 p-2 ${tone}`}>
                  <Icon className="h-4 w-4" />
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Card className="space-y-4 p-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full rounded-xl border border-gray-200 py-2.5 pl-9 pr-3 text-sm focus:border-brand-green focus:outline-none focus:ring-2 focus:ring-brand-green/20"
            placeholder="Search reference or service..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label htmlFor="tx-category" className="mb-1.5 block text-xs font-medium text-brand-muted">
              Category
            </label>
            <select
              id="tx-category"
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value as TransactionCategory | '');
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="tx-status" className="mb-1.5 block text-xs font-medium text-brand-muted">
              Status
            </label>
            <select
              id="tx-status"
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as TransactionStatus | '');
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            >
              <option value="">All statuses</option>
              <option value="success">Successful</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div>
            <label htmlFor="tx-type" className="mb-1.5 block text-xs font-medium text-brand-muted">
              Type
            </label>
            <select
              id="tx-type"
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
              value={type}
              onChange={(e) => {
                setType(e.target.value as TransactionType | '');
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            >
              <option value="">All types</option>
              <option value="debit">Debit</option>
              <option value="credit">Credit</option>
            </select>
          </div>
          <div>
            <label htmlFor="tx-period" className="mb-1.5 block text-xs font-medium text-brand-muted">
              Period
            </label>
            <select
              id="tx-period"
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
              value={period}
              onChange={(e) => {
                setPeriod(e.target.value as typeof period);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="all">All time</option>
            </select>
          </div>
        </div>
      </Card>

      {loading ? (
        <Loader label="Loading transactions..." />
      ) : transactions.length === 0 ? (
        <Card className="p-8 text-center text-sm text-gray-500">No transactions match your filters.</Card>
      ) : (
        <div className="space-y-2">
          {transactions.map((tx) => (
            <TransactionListItem key={tx.id} transaction={tx} onClick={() => setSelected(tx)} />
          ))}
        </div>
      )}

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
              className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <TransactionReceiptModal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        transaction={selected}
      />
    </div>
  );
}
